import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Edit2, X, StickyNote, Save, Download } from "lucide-react";
import ArchiveButton from "../components/ArchiveButton";
import ReportActions from "../components/ReportActions";
import { useTaxYear } from "../contexts/TaxYearContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Notes() {
  const { defaultTaxYear } = useTaxYear();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", content: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  const fetchNotes = async () => {
    try {
      const { data } = await axios.get(`${API}/notes`, { withCredentials: true });
      setNotes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await axios.post(`${API}/notes`, form, { withCredentials: true });
      setForm({ title: "", content: "" });
      setShowForm(false);
      fetchNotes();
    } catch {}
  };

  const handleUpdate = async (id) => {
    try {
      await axios.patch(`${API}/notes/${id}`, editForm, { withCredentials: true });
      setEditId(null);
      fetchNotes();
    } catch {}
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/notes/${id}`, { withCredentials: true });
    setNotes(prev => prev.filter(n => (n.id || n._id) !== id));
  };

  const REMINDERS = [
    "Upload all bank statements by January",
    "Collect W2s from employees",
    "Download CACFP annual report",
    "Print all check payment records",
    "Compile Sam's Club & Giant Eagle food receipts",
    "Download ADP payroll report",
    "Request Citizens Business account annual report",
    "Collect insurance policy copies (Travelers, Utica, Hartford)",
    "Review SBA loan payment statements",
    "Get Simple IRA deduction summary from ADP",
    "Start TurboTax with all organized data"
  ];

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Notes & Reminders</h1>
          <p className="text-slate-500 text-sm mt-1">Keep track of tax prep tasks and important notes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportActions
            taxYear={defaultTaxYear}
            category="Notes"
            source="/api/notes/pdf"
            sourceParams={{}}
            titleSuffix={`${notes.length} note${notes.length === 1 ? "" : "s"}`}
            filenameHint={`notes-${new Date().toISOString().slice(0,10)}.pdf`}
            disabled={notes.length === 0}
            testId="notes-all"
          />
          <button onClick={() => setShowForm(!showForm)}
            data-testid="add-note-btn"
            className="flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-all">
            <Plus className="w-4 h-4" /> Add Note
          </button>
        </div>
      </div>

      {/* Add Note Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="note-create-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>New Note</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <input
            type="text"
            placeholder="Note title"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            required
            data-testid="note-title-input"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          />
          <textarea
            placeholder="Note content..."
            value={form.content}
            onChange={e => setForm({ ...form, content: e.target.value })}
            rows={4}
            data-testid="note-content-input"
            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] resize-none"
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm">Cancel</button>
            <button type="submit" data-testid="save-note-btn"
              className="bg-[#2563EB] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] transition-all flex items-center gap-2">
              <Save className="w-4 h-4" /> Save Note
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notes */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-semibold text-slate-700 text-sm" style={{ fontFamily: "Manrope" }}>My Notes</h2>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}</div>
          ) : notes.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
              <StickyNote className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No notes yet. Click "Add Note" to create one.</p>
            </div>
          ) : notes.map(note => {
            const noteId = note.id || note._id;
            return (
            <div key={noteId} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all" data-testid={`note-card-${noteId}`}>
              {editId === noteId ? (
                <div className="space-y-3">
                  <input value={editForm.title || note.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" />
                  <textarea value={editForm.content || note.content} onChange={e => setEditForm({ ...editForm, content: e.target.value })}
                    rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] resize-none" />
                  <div className="flex gap-2">
                    <button onClick={() => handleUpdate(noteId)} className="bg-[#2563EB] text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#1D4ED8]" data-testid={`save-note-edit-${noteId}`}>Save</button>
                    <button onClick={() => setEditId(null)} className="text-slate-500 px-4 py-1.5 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>{note.title}</h3>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => { setEditId(noteId); setEditForm({ title: note.title, content: note.content }); }} className="text-slate-300 hover:text-[#2563EB] transition-colors" data-testid={`edit-note-${noteId}`}><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(noteId)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`delete-note-${noteId}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {note.content && <p className="text-slate-500 text-sm mt-2 leading-relaxed">{note.content}</p>}
                  <p className="text-slate-300 text-xs mt-3">{new Date(note.updated_at).toLocaleDateString()}</p>
                </>
              )}
            </div>
            );
          })}
        </div>

        {/* Tax Prep Checklist */}
        <div>
          <h2 className="font-semibold text-slate-700 text-sm mb-4" style={{ fontFamily: "Manrope" }}>Tax Prep Checklist</h2>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="tax-prep-checklist">
            <div className="space-y-2.5">
              {REMINDERS.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 border-2 border-slate-300 rounded mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 text-xs leading-snug">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
